import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { format as formatDate, parseISO } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { BottomSheetContentLayout, CloseButton, Column, MobileSummaryCard, Row, TextField, TextFieldWithDropdown, Typography, useDevice, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { BillingStageScheduleType, StageScheduleContext, StagesValueType } from '~/models';
import { utils } from '~/utils';
import { defaultScheduleForType } from './scheduleForType';
import { getScheduleLabel } from './scheduleLabel';
import { StageScheduleControl } from './StageScheduleControl';
const valueTypeTextMap = {
    [StagesValueType.Percent]: '%',
    [StagesValueType.Amount]: '$'
};
export const GetAutomaticStageRowTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const VALUE_WIDTH = 140;
const computeDollarAmount = (stage, valueType, totalRate)=>valueType === StagesValueType.Percent ? totalRate.mul(stage.value).div(100) : stage.value;
const StageEditSheet = (param)=>{
    let { label, stage, valueType, totalRate, isFirst, isLast, context, effectiveDate, today, helperText, saveLabel, billedTexts, valueTestId, scheduleTestId, billedAmountTestId, rootTestId, onSave, onClose } = param;
    var _tempStage_stageSchedule;
    const [tempStage, setTempStage] = useState(stage);
    const [tempValueType, setTempValueType] = useState(valueType);
    const hasError = !!helperText;
    const valueReadOnly = isLast;
    const dollarAmount = computeDollarAmount(tempStage, tempValueType, totalRate);
    const valueField = isFirst ? /*#__PURE__*/ _jsx(TextFieldWithDropdown, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        collapseHelperText: true,
        label: label,
        testId: valueTestId,
        error: hasError,
        helperText: helperText,
        value: {
            text: tempStage.value.toString(),
            option: tempValueType
        },
        options: Object.entries(valueTypeTextMap).map((param)=>{
            let [value, text] = param;
            return {
                value: value,
                label: text
            };
        }),
        deriveProps: (option)=>({
                isMoney: option === StagesValueType.Amount,
                isPercentage: option === StagesValueType.Percent
            }),
        onChange: (next)=>{
            const parsed = new Decimal(next.text || 0).toDecimalPlaces(2);
            setTempStage((prev)=>_object_spread_props(_object_spread({}, prev), {
                    value: parsed
                }));
            setTempValueType(next.option);
        }
    }) : /*#__PURE__*/ _jsx(TextField, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        collapseHelperText: true,
        label: label,
        testId: valueTestId,
        error: hasError,
        helperText: helperText,
        isMoney: tempValueType === StagesValueType.Amount,
        isPercentage: tempValueType === StagesValueType.Percent,
        value: tempStage.value.toString(),
        onChange: (event)=>{
            const parsed = new Decimal(event.target.value || 0).toDecimalPlaces(2);
            setTempStage((prev)=>_object_spread_props(_object_spread({}, prev), {
                    value: parsed
                }));
        },
        inputProps: valueReadOnly ? {
            readOnly: true
        } : undefined,
        suffix: /*#__PURE__*/ _jsx(Typography, {
            children: valueTypeTextMap[tempValueType]
        })
    });
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        header: {
            title: label,
            gutter: 'm',
            paddingBottom: 24
        },
        footer: {
            paddingTop: 24,
            paddingBottom: 16,
            primaryAction: {
                text: saveLabel,
                onClick: ()=>{
                    onSave({
                        stage: tempStage,
                        valueType: tempValueType
                    });
                    onClose();
                },
                noBi: true
            }
        },
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            style: {
                paddingTop: 8,
                paddingBottom: 8
            },
            testId: rootTestId,
            children: [
                valueField,
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 4,
                    alignItems: "center",
                    wrap: false,
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            testId: billedAmountTestId,
                            children: [
                                utils.formatDollarAmount(dollarAmount),
                                " ",
                                billedTexts.billed
                            ]
                        }),
                        /*#__PURE__*/ _jsx(StageScheduleControl, {
                            testId: scheduleTestId,
                            schedule: (_tempStage_stageSchedule = tempStage.stageSchedule) !== null && _tempStage_stageSchedule !== void 0 ? _tempStage_stageSchedule : defaultScheduleForType(BillingStageScheduleType.Manual),
                            context: context,
                            effectiveDate: effectiveDate,
                            today: today,
                            onChange: (stageSchedule)=>setTempStage((prev)=>_object_spread_props(_object_spread({}, prev), {
                                        stageSchedule
                                    }))
                        })
                    ]
                })
            ]
        })
    });
};
export const AutomaticStageRow = (param)=>{
    let { testId, stage, stageNumber, context, effectiveDate, today, isFirst, isLast, canRemove, valueType, totalRate, helperText, disabled, onChange, onValueTypeChange, onRemove } = param;
    var _stage_stageSchedule;
    const { AutomaticStageRowTestIds } = useMemo(()=>GetAutomaticStageRowTID(testId), [
        testId
    ]);
    const { t: stagesTexts } = useLocaleKeys('billing.stages');
    const { isMobileDevice } = useDevice();
    const { pushBottomSheetPage, hideBottomSheet } = useOverlayContext();
    const label = stagesTexts.stageLabel({
        stageNumber
    });
    const valueDisabled = disabled || !isMobileDevice && isLast;
    const dollarAmount = computeDollarAmount(stage, valueType, totalRate);
    const hasError = !!helperText;
    const fieldWidth = VALUE_WIDTH;
    const valueField = isFirst ? /*#__PURE__*/ _jsx(TextFieldWithDropdown, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        collapseHelperText: true,
        label: label,
        testId: AutomaticStageRowTestIds.Value(),
        disabled: valueDisabled,
        width: fieldWidth,
        minWidth: fieldWidth,
        error: hasError,
        helperText: helperText,
        value: {
            text: stage.value.toString(),
            option: valueType
        },
        options: Object.entries(valueTypeTextMap).map((param)=>{
            let [value, text] = param;
            return {
                value: value,
                label: text
            };
        }),
        deriveProps: (option)=>({
                isMoney: option === StagesValueType.Amount,
                isPercentage: option === StagesValueType.Percent
            }),
        onChange: (next)=>{
            const optionChanged = next.option !== valueType;
            const textChanged = next.text !== stage.value.toString();
            if (!optionChanged && !textChanged) return;
            if (textChanged) {
                const parsed = new Decimal(next.text || 0).toDecimalPlaces(2);
                onChange(_object_spread_props(_object_spread({}, stage), {
                    value: parsed
                }));
            }
            if (optionChanged) {
                onValueTypeChange(next.option);
            }
        }
    }) : /*#__PURE__*/ _jsx(TextField, {
        bi: {
            name: 'stage_value'
        },
        alwaysShrinkLabel: true,
        collapseHelperText: true,
        label: label,
        testId: AutomaticStageRowTestIds.Value(),
        disabled: valueDisabled,
        width: fieldWidth,
        minWidth: fieldWidth,
        error: hasError,
        helperText: helperText,
        isMoney: valueType === StagesValueType.Amount,
        isPercentage: valueType === StagesValueType.Percent,
        value: stage.value.toString(),
        onChange: (event)=>{
            const parsed = new Decimal(event.target.value || 0).toDecimalPlaces(2);
            onChange(_object_spread_props(_object_spread({}, stage), {
                value: parsed
            }));
        },
        suffix: /*#__PURE__*/ _jsx(Typography, {
            children: valueTypeTextMap[valueType]
        })
    });
    const removeButton = canRemove && !disabled && /*#__PURE__*/ _jsx(CloseButton, {
        bi: {
            name: 'remove_stage'
        },
        skin: "transparent",
        testId: AutomaticStageRowTestIds.Remove(),
        onClick: onRemove
    });
    const billedLine = /*#__PURE__*/ _jsxs(Row, {
        gap: 4,
        alignItems: "center",
        wrap: false,
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                testId: AutomaticStageRowTestIds.BilledAmount(),
                children: [
                    utils.formatDollarAmount(dollarAmount),
                    " ",
                    stagesTexts.schedule.inline.billed()
                ]
            }),
            /*#__PURE__*/ _jsx(StageScheduleControl, {
                testId: AutomaticStageRowTestIds.Schedule(),
                schedule: (_stage_stageSchedule = stage.stageSchedule) !== null && _stage_stageSchedule !== void 0 ? _stage_stageSchedule : defaultScheduleForType(BillingStageScheduleType.Manual),
                context: context,
                effectiveDate: effectiveDate,
                today: today,
                disabled: disabled,
                onChange: (stageSchedule)=>onChange(_object_spread_props(_object_spread({}, stage), {
                        stageSchedule
                    }))
            })
        ]
    });
    if (isMobileDevice && context !== StageScheduleContext.Template) {
        return /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            testId: AutomaticStageRowTestIds.Root(),
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "start",
                    wrap: false,
                    children: [
                        /*#__PURE__*/ _jsx(Column, {
                            style: {
                                flex: 1
                            },
                            children: valueField
                        }),
                        removeButton && /*#__PURE__*/ _jsx(Row, {
                            alignItems: "center",
                            style: {
                                alignSelf: 'flex-start',
                                height: 56
                            },
                            children: removeButton
                        })
                    ]
                }),
                billedLine
            ]
        });
    }
    if (isMobileDevice) {
        var _stage_stageSchedule1, _ref;
        var _getScheduleLabel;
        const valuePart = valueType === StagesValueType.Percent ? "".concat(stage.value.toString(), "%") : utils.formatDollarAmount(stage.value);
        const effectiveSchedule = (_stage_stageSchedule1 = stage.stageSchedule) !== null && _stage_stageSchedule1 !== void 0 ? _stage_stageSchedule1 : defaultScheduleForType(BillingStageScheduleType.Manual);
        const scheduleLabel = (_ref = (_getScheduleLabel = getScheduleLabel({
            schedule: effectiveSchedule,
            context,
            texts: stagesTexts.schedule,
            today
        })) === null || _getScheduleLabel === void 0 ? void 0 : _getScheduleLabel.label) !== null && _ref !== void 0 ? _ref : '';
        const scheduleSuffix = (()=>{
            if (effectiveSchedule.type === BillingStageScheduleType.OnDate && effectiveSchedule.onDate.date && (context !== StageScheduleContext.Amendment || effectiveSchedule.onDate.date !== today)) {
                return " ".concat(formatDate(parseISO(effectiveSchedule.onDate.date), 'MMM d, yyyy'));
            }
            if (effectiveSchedule.type === BillingStageScheduleType.RelativeToEffectiveDate) {
                return '';
            }
            return '';
        })();
        const schedulePrefix = effectiveSchedule.type === BillingStageScheduleType.RelativeToEffectiveDate ? "".concat(effectiveSchedule.relativeToEffectiveDate.days, " ").concat(stagesTexts.schedule.inline.days({
            count: effectiveSchedule.relativeToEffectiveDate.days
        }), " ") : '';
        const billedAmountText = utils.formatDollarAmount(dollarAmount);
        const summary = "".concat(valuePart, " • ").concat(billedAmountText, " ").concat(stagesTexts.schedule.inline.billed(), " ").concat(schedulePrefix).concat(scheduleLabel).concat(scheduleSuffix).trim();
        const openEditSheet = ()=>{
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(StageEditSheet, {
                    label: label,
                    stage: stage,
                    valueType: valueType,
                    totalRate: totalRate,
                    isFirst: isFirst,
                    isLast: isLast,
                    context: context,
                    effectiveDate: effectiveDate,
                    today: today,
                    helperText: helperText,
                    saveLabel: stagesTexts.editMobile.save(),
                    billedTexts: {
                        billed: stagesTexts.schedule.inline.billed()
                    },
                    valueTestId: AutomaticStageRowTestIds.Value(),
                    scheduleTestId: AutomaticStageRowTestIds.Schedule(),
                    billedAmountTestId: AutomaticStageRowTestIds.BilledAmount(),
                    rootTestId: AutomaticStageRowTestIds.EditSheet(),
                    onSave: (param)=>{
                        let { stage: nextStage, valueType: nextValueType } = param;
                        onChange(nextStage);
                        if (nextValueType !== valueType) {
                            onValueTypeChange(nextValueType);
                        }
                    },
                    onClose: hideBottomSheet
                })
            });
        };
        return /*#__PURE__*/ _jsx(MobileSummaryCard, {
            testId: AutomaticStageRowTestIds.Root(),
            error: hasError,
            items: [
                {
                    title: label,
                    bodyEllipsis: true,
                    body: /*#__PURE__*/ _jsxs(Column, {
                        gap: 2,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                testId: AutomaticStageRowTestIds.BilledAmount(),
                                children: summary
                            }),
                            hasError && /*#__PURE__*/ _jsx(Typography, {
                                ellipsis: true,
                                variant: "paragraph2",
                                skin: "error",
                                children: helperText
                            })
                        ]
                    }),
                    actions: compact([
                        !disabled && {
                            type: 'edit',
                            testId: AutomaticStageRowTestIds.EditTrigger(),
                            onClick: openEditSheet,
                            bi: {
                                name: 'edit_stage'
                            }
                        },
                        canRemove && !disabled && {
                            type: 'remove',
                            testId: AutomaticStageRowTestIds.Remove(),
                            onClick: onRemove,
                            bi: {
                                name: 'remove_stage'
                            }
                        }
                    ])
                }
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        testId: AutomaticStageRowTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    valueField,
                    billedLine
                ]
            }),
            removeButton
        ]
    });
};
